// @TODO add differing contact information
import React, { Fragment, useContext, useEffect, useState } from "react"
import { IPtsCourseSection } from "../api/get-pts-sections"
import { PtsCartContext } from "../context/PtsCartContext"
import dateToString from "../utils/date-to-string"
import formatTimeRange from "../utils/format-time-range"
import {
  buildingCodeToBuildingLetter,
  campusNameFromCode,
} from "../utils/georgian"
import LoadingSpinner from "./loading-spinner"
import { Disclosure } from "@headlessui/react"
import ChevronIcon from "../icons/chevron"
import classNames from "../utils/classnames"

interface IPtsCourseSectionProps {
  code: string
  number: string
}

const PtsCourseSections = ({
  code,
  number,
}: IPtsCourseSectionProps): JSX.Element => {
  // console.log(`pts course sections`)
  const [sections, setSections] = useState<Array<IPtsCourseSection>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const {
    setPtsCartOpen,
    addToPtsCart,
    removeFromPtsCart,
    sections: cartSections,
  } = useContext(PtsCartContext)
  useEffect(() => {
    // get courses from api whenever code/number changes (first load) in frontend
    const getPtsCourseSections = async (): Promise<void> => {
      const url = `/api/get-pts-sections?code=${code}&number=${number}`
      const res = await fetch(url)
      const data = await res.json()
      // console.log(`sections`, data)
      if (data?.sections?.length > 0) {
        setLoading(false)
        setSections(data.sections)
        setError(null)
      } else {
        setLoading(false)
        setSections([])
        setError(new Error(`No sections found for ${code} ${number}`))
      }
    }
    getPtsCourseSections()
  }, [code, number])
  // console.log(`pts sectinos`, { sections })
  if (error || (!loading && sections.length === 0))
    return <div>No courses currently running. Please try again later</div>
  return loading ? (
    <div className="flex gap-2">
      <LoadingSpinner />
      <span>Loading Course Sections</span>
    </div>
  ) : (
    <div className="flex flex-col">
      {sections.map(
        section =>
          section.ShowStatus && (
            <Disclosure key={section.CRN}>
              {({ open }): JSX.Element => (
                <>
                  <Disclosure.Button
                    className={classNames(
                      `bg-lightgray hover:bg-gray-50 text-left px-4 py-4 flex gap-2 border mt-[-1px]`,
                      open ? ` border-b-0` : ``
                    )}
                  >
                    <ChevronIcon
                      className={classNames(
                        `w-4 h-4 mt-1 -ml-1 transition-transform`,
                        open ? `rotate-90` : ``
                      )}
                    />
                    <span className="flex-col gap-1">
                      <span className="text-blue font-bold block">
                        {section.Title} - {section.SubjCode}
                        {` `}
                        {section.CourseNumber}
                      </span>
                      <span className="block">
                        {dateToString(
                          new Date(section.ClassStartDateTimestamp)
                        )}
                      </span>
                      <span className="block">
                        <span className="uppercase font-bold">
                          {section.Status}
                        </span>
                      </span>
                    </span>
                  </Disclosure.Button>
                  <Disclosure.Panel className="border-x">
                    <div
                      key={section.CRN}
                      className="flex flex-col bg-lightgray p-4"
                    >
                      <PTSSectionDetail section={section} />
                    </div>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          )
      )}
    </div>
  )
}

export const PTSSectionDetail = ({ section }) => {
  const {
    setPtsCartOpen,
    addToPtsCart,
    removeFromPtsCart,
    sections: cartSections,
  } = useContext(PtsCartContext)
  return (
    <>
      <div key={section.CRN} className="flex flex-col">
        <div className="uppercase mb-2">
          {section.Title} - {section.SubjCode}
          {` `}
          {section.CourseNumber}
        </div>
        <div className="flex gap-2 mb-2">
          <div>Fee:</div>
          <div>${section.Fees}</div>
        </div>

        <div className="flex gap-2 mb-2">
          <div>Course registration number: </div>
          <div>{section.CRN}</div>
        </div>

        <div className="flex gap-2">
          <div className="font-semibold">Start: </div>
          <div>{dateToString(new Date(section.ClassStartDateTimestamp))}</div>
        </div>
        <div className="flex gap-2 mb-2">
          <div className="font-semibold">End: </div>
          <div>{dateToString(new Date(section.ClassEndDateTimestamp))}</div>
        </div>

        <div className="font-semibold">Class times</div>
        <div className="flex flex-col divide-y divide-white border border-white mb-2">
          <div className="grid grid-cols-12 gap-2 divide-x divide-white">
            <div className="col-span-4 py-1 px-2 font-semibold">Days</div>
            <div className="col-span-3 py-1 px-2 font-semibold">Times</div>
            <div className="col-span-4 py-1 px-2 font-semibold">
              Building - Room
            </div>
          </div>

          {section.Meetings.map((meeting, index) =>
            meeting.Description === `Online` ? (
              <div
                key={index}
                className="grid grid-cols-12 gap-2 divide-x divide-white"
              >
                {meeting.Days[0] ? (
                  <>
                    <div className="col-span-4 py-1 px-2 overflow-hidden">
                      {meeting.Days.join(`, `)}
                    </div>
                    <div className="col-span-3 py-1 px-2">
                      {formatTimeRange(meeting.BeginTime, meeting.EndTime)}
                    </div>
                    <div className="col-span-5 py-1 px-2">Online</div>
                  </>
                ) : (
                  <div
                    key={index}
                    className="col-span-12 py-1 px-2 w-full text-center"
                  >
                    Online
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-12 gap-2 divide-x divide-white">
                <div className="col-span-4 py-1 px-2 overflow-hidden">
                  {meeting.Days.join(`, `)}
                </div>
                <div className="col-span-3 py-1 px-2">
                  {formatTimeRange(meeting.BeginTime, meeting.EndTime)}
                </div>
                <div className="col-span-5 py-1 px-2">
                  {meeting.RoomCode === `` ? (
                    <>To be scheduled. Check back.</>
                  ) : (
                    <>{`${buildingCodeToBuildingLetter(
                      meeting.BuildingCode
                    )} - ${meeting.RoomCode}`}</>
                  )}
                </div>
              </div>
            )
          )}
        </div>

        {section.Meetings[0]?.Description === `GC Flex` && (
          <p className="mt-2">
            <strong>NOTE:</strong> This course is delivered online and in
            person.
            {` `}
            <a
              href="https://ecampusontario.pressbooks.pub/studentgcflex/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about GC Flex.
            </a>
          </p>
        )}

        <div className="flex gap-2 mb-2">
          <div className="">Campus: </div>
          <div>{campusNameFromCode(section.CampCode)}</div>
        </div>

        <div className="text-right">
          {cartSections.findIndex(s => section.CRN === s.CRN) !== -1 ? (
            <>
              <button
                onClick={(): void => setPtsCartOpen(true)}
                className="bg-green hover:bg-blue text-white p-2"
              >
                View Cart
              </button>
              <button
                className="bg-green hover:bg-blue text-white p-2 ml-2"
                onClick={(): void => removeFromPtsCart(section)}
                data-crn={section.CRN}
                data-subj={section.SubjCode}
                data-number={section.CourseNumber}
                data-title={section.Title}
                data-add-crn={section.AddHash}
                data-remove-query={section.RemoveHash}
                data-event-category="Part-time Studies"
                data-event-action="remove from cart"
                data-event-label={`${section.SubjCode} ${section.CourseNumber} / CRN: ${section.CRN}`}
                data-event-value={section.Fees}
              >
                Remove from cart
              </button>
            </>
          ) : section.Status === `Open` ? (
            <button
              className="bg-green hover:bg-blue text-white p-2"
              onClick={(): void => addToPtsCart(section)}
              data-crn={section.CRN}
              data-subj={section.SubjCode}
              data-number={section.CourseNumber}
              data-title={section.Title}
              data-add-crn={section.AddHash}
              data-remove-query={section.RemoveHash}
              data-event-category="Part-time Studies"
              data-event-action="add to cart"
              data-event-label={`${section.SubjCode} ${section.CourseNumber} / CRN: ${section.CRN}`}
              data-event-value={section.Fees}
            >
              Add to cart
            </button>
          ) : (
            <span className="uppercase font-bold">{section.Status}</span>
          )}
        </div>
      </div>
    </>
  )
}

export default PtsCourseSections
